'use client';

import type { CSSProperties, ImgHTMLAttributes, Ref } from 'react';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { cx } from '../utils/cx';
import { getFilesOrigin, resolveAssetUrl, sanitizeUrl } from '../utils/asset-url';

export interface AvatarProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  /** Image URL (optional: if missing/fails, fallback is shown). */
  src?: string;

  /**
   * Alt text for the image.
   * If omitted/empty, the avatar is treated as decorative.
   */
  alt?: string;

  /**
   * Size as a number (interpreted as px) or a CSS length string (e.g. "2.5rem").
   * Also supports token sizes: "xs" | "sm" | "md" | "lg" | "xl" | "2xl".
   * @default 40
   */
  size?: number | string;

  /** Fallback initials/text if image is missing or fails. */
  fallback?: string;

  /**
   * Optional background for fallback circle.
   * Recommended usage:
   * - CSS color: "#111827", "rgb(...)", "var(--...)".
   * - If you want Tailwind classes, prefer using `className`.
   */
  bg?: string;

  /**
   * Optional text color for fallback initials.
   * Recommended usage: CSS color values or CSS variables. Prefer `className` for Tailwind.
   */
  color?: string;

  /** Optional class for the wrapper. */
  className?: string;

  /** Optional class for the img element. */
  imgClassName?: string;
}

const TOKEN_SIZE_MAP: Record<string, number> = {
  xs: 20,
  sm: 28,
  md: 40,
  lg: 56,
  xl: 72,
  '2xl': 96,
};

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function resolveAvatarSize(size: number | string): string {
  if (typeof size === 'number') return `${size}px`;

  const raw = size.trim();
  const token = raw.toLowerCase();

  if (token in TOKEN_SIZE_MAP) return `${TOKEN_SIZE_MAP[token]}px`;

  if (/^\d+(\.\d+)?$/.test(raw)) return `${raw}px`;

  return raw;
}

function resolveAvatarSizePx(size: number | string): number | null {
  if (typeof size === 'number' && Number.isFinite(size)) return size;

  const raw = String(size).trim().toLowerCase();
  if (!raw) return null;

  if (raw in TOKEN_SIZE_MAP) return TOKEN_SIZE_MAP[raw] ?? null;

  if (/^\d+(\.\d+)?$/.test(raw)) return Number.parseFloat(raw);

  if (/^\d+(\.\d+)?px$/.test(raw)) return Number.parseFloat(raw);

  // Approximate rem to px (assumes 16px root).
  if (/^\d+(\.\d+)?rem$/.test(raw)) return Number.parseFloat(raw) * 16;

  return null;
}

function initialsFromAlt(alt?: string): string | undefined {
  const cleaned = (alt ?? '').trim();
  if (!cleaned) return undefined;

  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return undefined;

  const first = parts[0]?.[0] ?? '';
  const second = (parts[1]?.[0] ?? parts[0]?.[1] ?? '') || '';
  const initials = `${first}${second}`.trim();

  return initials ? initials.toUpperCase() : undefined;
}

function isCssColorValue(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  return (
    v.startsWith('#') ||
    v.startsWith('rgb(') ||
    v.startsWith('rgba(') ||
    v.startsWith('hsl(') ||
    v.startsWith('hsla(') ||
    v.startsWith('var(')
  );
}

/**
 * Avatar
 * - Root-relative src resolved against Files host.
 * - Fallback initials derived from `fallback` or `alt`.
 * - Decorative when alt is empty.
 */
export const Avatar = forwardRef(function Avatar(
  { src, alt = '', size = 40, className, imgClassName, fallback, bg, color, onError, onLoad, style, ...rest }: AvatarProps,
  ref: Ref<HTMLImageElement>,
) {
  const [hasError, setHasError] = useState(false);

  const filesOrigin = useMemo(() => getFilesOrigin(), []);
  const resolvedSrc = useMemo(() => {
    if (!src) return '';
    return resolveAssetUrl(src, filesOrigin);
  }, [src, filesOrigin]);

  const safeSrc = useMemo(() => sanitizeUrl(resolvedSrc), [resolvedSrc]);

  useEffect(() => {
    setHasError(false);
  }, [safeSrc]);

  const resolvedStyle = useMemo(() => {
    const dimension = resolveAvatarSize(size);
    return { width: dimension, height: dimension, ...style } as CSSProperties;
  }, [size, style]);

  const showFallback = !safeSrc || hasError;
  const derivedFallback = fallback?.trim() || initialsFromAlt(alt) || '?';

  const isDecorative = alt.trim().length === 0;

  const fallbackTextStyle = useMemo((): CSSProperties => {
    const px = resolveAvatarSizePx(size);
    if (!px) return { lineHeight: 1 };

    const fontSize = clampNumber(Math.round(px * 0.42), 10, 22);
    return { fontSize, lineHeight: 1 };
  }, [size]);

  const fallbackColors: CSSProperties = useMemo(() => {
    const out: CSSProperties = {};
    if (typeof bg === 'string' && isCssColorValue(bg)) out.backgroundColor = bg.trim();
    if (typeof color === 'string' && isCssColorValue(color)) out.color = color.trim();
    return out;
  }, [bg, color]);

  return (
    <span
      className={cx(
        'relative inline-flex shrink-0 overflow-hidden rounded-full',
        'bg-secondary ring-1 ring-secondary_alt ring-inset',
        className,
      )}
      style={resolvedStyle}
    >
      {!showFallback ? (
        <img
          {...rest}
          ref={ref}
          src={safeSrc}
          alt={alt}
          loading={rest.loading ?? 'lazy'}
          decoding={rest.decoding ?? 'async'}
          draggable={rest.draggable ?? false}
          referrerPolicy={rest.referrerPolicy ?? 'no-referrer'}
          className={cx('h-full w-full object-cover', imgClassName)}
          onLoad={(e) => {
            setHasError(false);
            onLoad?.(e);
          }}
          onError={(e) => {
            setHasError(true);
            onError?.(e);
          }}
        />
      ) : (
        <span
          className="flex h-full w-full select-none items-center justify-center font-semibold text-secondary"
          style={{ ...fallbackTextStyle, ...fallbackColors }}
          role={!isDecorative ? 'img' : undefined}
          aria-label={!isDecorative ? alt : undefined}
          aria-hidden={isDecorative || undefined}
        >
          {derivedFallback.slice(0, 2).toUpperCase()}
        </span>
      )}
    </span>
  );
});

Avatar.displayName = 'Avatar';
